import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import * as screenfull from 'screenfull';
import { DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView } from 'wc-visualisation';
import { downloadCsv, arrayToCsv, Table, Icon, Tooltip, formatDate, formatNumber } from 'wc-lib';
import { DerInstallationTypeList, DerSourceList } from '../config';
import { DerTotalsData, DerData } from './der-data';

require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');
require('wc-lib/icons/electricity.svg');
require('wc-lib/icons/aemo_battery_storage.svg');
require('wc-lib/icons/aemo_solar_farm.svg');

@Component({
    template: require('./der-sites.html'),
    name: 'der-sites',
    components: {
        'vis-visualisation': Visualisation,
        'vis-toolbar': Toolbar,
        'wc-icon': Icon,
        'vis-table': Table,
        'vis-chart': Chart,
        'vis-filter-bar': FilterBar,
        'wc-tooltip': Tooltip
    }
})
export class DerSites extends Vue {

    isFullscreen = false;
    titleTotalsChart = 'Distributed Energy Resource totals';
    titleYearlyAdditionsChart = 'Additions each year';
    titleYearlyCumulativeChart = 'Cumulative by year';
    totalsData: Array<any> = [];
    yearlyAdditionsData: Array<any> = [];
    yearlyCumulativeData: Array<any> = [];
    dynamicViewTotalsData: CustomDynamicView<any>;
    dynamicViewYearlyData: CustomDynamicView<any>;
    dynamicViewPeriods: CustomDynamicView<any>;
    chartObject = null;
    totalsChartConfig: any = null;
    yearlyAdditionsChartObject = null;
    yearlyCumulativeChartObject = null;
    yearlyAdditionsChartConfig: any = null;
    yearlyCumulativeChartConfig: any = null;
    chartCursorCallback = null;
    intervalRefresh: any;
    derInstallationTypeList = [...DerInstallationTypeList];
    derSourceList = [...DerSourceList];
    derElectricityRegions = [
        { label: 'All regions', value: 'ALL' },
        { label: 'NSW', value: 'NSW' },
        { label: 'QLD', value: 'QLD' },
        { label: 'VIC', value: 'VIC' },
        { label: 'SA', value: 'SA' },
        { label: 'TAS', value: 'TAS' },
        { label: 'ACT', value: 'ACT' }
    ];
    selectedSource: any = 'ALL';
    selectedRegion: any = 'ALL';
    selectedInstallationType: any = 'ALL';
    selectedDerValueType: string = 'RATEDCAPACITY';

    //derPeriodList = [];
    derAllPeriodList = [];

    created() {

        if (screenfull.isEnabled) {
            (screenfull as any).onchange(() => {
                this.isFullscreen = (screenfull as any).isFullscreen;
            });
        }

        this.fetchData();
        //Uncomment below if regular refresh ever needed.
        //this.intervalRefresh = setInterval(() => {
        //    this.fetchData();
        //}, 300000);
    }

    async fetchData() {
        let dataId = await DataManager.addEndPoint('../../data/der/DER-DATA.csv', undefined, undefined, undefined, 'csv'); //, 'yyyy-MM-dd', ['DATETIME']);
        this.dynamicViewTotalsData = DataManager.getDynamicView(dataId, "DerTotalsData");
        this.dynamicViewYearlyData = DataManager.getDynamicView(dataId, "DerYearlyData");
        this.applyTotalsFilter();
        this.applyYearlyFilters();
    }

    applyTotalsFilter() {
        this.dynamicViewTotalsData.removeFilters();
        // Ensure that WA data is not displayed
        this.dynamicViewTotalsData.applyWhere(function aCustomFilter(obj) {
            return obj.State != 'WA';
        });
        this.dynamicViewTotalsData.applyFind({ 'Period': 'TOTAL' });
        if (this.selectedRegion != 'ALL')
            this.dynamicViewTotalsData.applyFind({ 'State': this.selectedRegion });
        if (this.selectedInstallationType != 'ALL') {
            this.dynamicViewTotalsData.applyFind({ 'InstallationType': this.selectedInstallationType });
        }
        if (this.selectedSource != 'ALL') {
            this.dynamicViewTotalsData.applyFind({ 'Source': this.selectedSource });
        }

        if (this.selectedDerValueType === 'STORAGECAPACITY') {
            this.selectedSource = 'BATTERY'; //Only batteries can have storage capacity
        }

        let dataView = this.dynamicViewTotalsData.data();
        let data: DerTotalsData[] = new Array;

        dataView.forEach(item => {
            let requiredSource = this.selectedSource;
            let requiredInstallationType = this.selectedInstallationType;
            let rec: DerTotalsData = null;

            rec = data.find(element => ((element.State == item.State) && (element.Source == requiredSource) && (element.InstallationType == requiredInstallationType)));

            if (rec) {
                rec.NoOfUnits = Number(rec.NoOfUnits) + Number(item.NoOfUnits);
                rec.RatedCapacity = Number(rec.RatedCapacity) + Number(item.RatedCapacity);
                rec.StorageCapacity = Number(rec.StorageCapacity) + Number(item.StorageCapacity);
            }
            else {
                let newrec = new DerTotalsData;
                newrec.State = item.State;
                newrec.NoOfUnits = item.NoOfUnits;
                newrec.RatedCapacity = item.RatedCapacity;
                newrec.StorageCapacity = item.StorageCapacity;
                newrec.Source = requiredSource;
                newrec.InstallationType = requiredInstallationType;
                switch (item.State) {
                    case 'QLD': newrec.Colour = "#A82140"; break;
                    case 'NSW': newrec.Colour = "#ADE0EE"; break;
                    case 'VIC': newrec.Colour = "#777DA7"; break;
                    case 'SA': newrec.Colour = "#FE5F55"; break;
                    case 'TAS': newrec.Colour = "#A1D978"; break;
                    case 'ACT': newrec.Colour = "#FFD565"; break;
                    default: newrec.Colour = "#0f143c";
                }
                data.push(newrec);
            }
        }
        );

        this.totalsData = data;
        this.totalsChartConfig = this.getTotalsChartConfig();
    }

    applyYearlyFilters() {
        this.dynamicViewYearlyData.removeFilters();
        this.dynamicViewYearlyData.applyWhere(function aCustomFilter(obj) {
            return obj.Period[0] == '2' && obj.State != 'WA'; //All years (ie not 'TOTAL'), exclude WA data
        });
        if (this.selectedRegion !== 'ALL')
            this.dynamicViewYearlyData.applyFind({ 'State': this.selectedRegion });
        if (this.selectedInstallationType != 'ALL') {
            this.dynamicViewYearlyData.applyFind({ 'InstallationType': this.selectedInstallationType });
        }
        if (this.selectedSource != 'ALL') {
            this.dynamicViewYearlyData.applyFind({ 'Source': this.selectedSource });
        }

        let dataView = this.dynamicViewYearlyData.data();
        let data: DerData[] = new Array;

        //Data already filtered to period at this stage
        //Summarise based on selections for each state

        dataView.forEach(item => {
            let requiredSource = this.selectedSource;
            let requiredInstallationType = this.selectedInstallationType;

            let rec: DerData = null;

            rec = data.find(element => ((element.Period === item.Period) && (element.State === item.State) && (element.Source === requiredSource) && (element.InstallationType === requiredInstallationType)));

            if (rec) {
                if (!isNaN(item.NoOfUnits))
                    rec.NoOfUnits = rec.NoOfUnits + parseInt(item.NoOfUnits);
                if (!isNaN(item.RatedCapacity))  
                    rec.RatedCapacity = rec.RatedCapacity + parseInt(item.RatedCapacity);
                if (!isNaN(item.StorageCapacity))  
                    rec.StorageCapacity = rec.StorageCapacity + parseInt(item.StorageCapacity);
            }
            else {
                let newrec = new DerData;
                newrec.Period = item.Period;
                newrec.State = item.State;
                if (!isNaN(item.NoOfUnits))
                    newrec.NoOfUnits = parseInt(item.NoOfUnits);
                if (!isNaN(item.RatedCapacity))  
                    newrec.RatedCapacity = parseInt(item.RatedCapacity);
                if (!isNaN(item.StorageCapacity))  
                    newrec.StorageCapacity = parseInt(item.StorageCapacity);
                newrec.Source = requiredSource;
                newrec.InstallationType = requiredInstallationType;
                switch (item.State) {
                    case 'QLD': newrec.Colour = "#A82140"; break;
                    case 'NSW': newrec.Colour = "#ADE0EE"; break;
                    case 'VIC': newrec.Colour = "#777DA7"; break;
                    case 'SA': newrec.Colour = "#FE5F55"; break;
                    case 'TAS': newrec.Colour = "#A1D978"; break;
                    case 'ACT': newrec.Colour = "#FFD565"; break;
                    default: newrec.Colour = "#0f143c";
                }
                newrec.LabelColour = newrec.Colour;
                data.push(newrec);
            }

        });

        this.yearlyAdditionsData = data.sort(function (a, b) {
            if (a.Period < b.Period) return -1;
            if (a.Period > b.Period) return 1;
            return 0;
        });

        this.yearlyCumulativeData = [];

        this.yearlyAdditionsChartConfig = this.getYearlyChartConfig(this.yearlyAdditionsData, this.yearlyAdditionsChartObject);

        //Calculate accumulated totals in a different dataset. 
        this.yearlyAdditionsData.slice().forEach(x => 
            {
                let d: DerData = new DerData;
                d = {...x};
                this.yearlyCumulativeData.push(d);
                let previousPeriods = this.yearlyAdditionsData.filter(e => { 
                    return ((e.Period < d.Period)&&(e.State === d.State)&&(e.Source === d.Source)&&(e.InstallationType === d.InstallationType)); 
                } );
                if (previousPeriods) {
                    previousPeriods.slice().forEach(element => {
                        d.RatedCapacity = (d.RatedCapacity + element.RatedCapacity);
                        d.StorageCapacity = (d.StorageCapacity + element.StorageCapacity);
                        d.NoOfUnits = (d.NoOfUnits + element.NoOfUnits);
                    });
                }
            }
        );

        this.yearlyCumulativeChartConfig = this.getYearlyChartConfig(this.yearlyCumulativeData, this.yearlyCumulativeChartObject);
    }

    @Watch('selectedRegion')
    @Watch('selectedPeriod')
    @Watch('selectedInstallationType')
    @Watch('selectedSource')
    @Watch('selectedDerValueType')
    filterChange() {
        this.applyTotalsFilter();
        this.applyYearlyFilters();
    }

    getTotalsChartConfig() {

        if ((this.chartObject) && (this.chartObject.listenersAdded))
            this.chartObject.removeListener(this.chartObject, 'drawn', this.chartCursorCallback);

        let valueField = 'RatedCapacity'; 
        let unitOfValue = 'kVA';
        let valueWidth = 75;
        if (this.selectedDerValueType === 'STORAGECAPACITY') {
            valueField = 'StorageCapacity';
            unitOfValue = 'kVAh';
            valueWidth = 70;
        }      
        let labelText = '[[title]]: [[value]] [[unitOfValue]]  ([[percents]]%)';
        if (this.selectedDerValueType === 'NOOFSITES') {
            valueField = 'NoOfUnits';
            labelText = '[[title]]: [[value]] units ([[percents]]%)';
            unitOfValue = '';
            valueWidth = 40;
        }

        this.chartObject = null;
        return {
            type: 'pie',
            titleField: "State",
            colorField: "Colour",
            categoryField: "State",
            valueField: valueField,
            innerRadius: "33%",
            legend: {
                enabled: true ,
                align: 'center',
                markerLabelGap: 10,
                valueWidth: valueWidth,
                valueText: `[[value]] ${unitOfValue}`
            },
            labelsEnabled: false,
            //labelText: labelText,
            precision: 0,
            thousandsSeparator: ','
        };
    }

    getYearlyChartConfig(yearlyData, chartObject) {
        if ((chartObject) && (chartObject.listenersAdded))
            chartObject.removeListener(chartObject, 'drawn', this.chartCursorCallback);

        let valueField = 'RatedCapacity'; 
        let title = 'kVA';
        let labelText = '[[RatedCapacity]]kVA';
        if (this.selectedDerValueType === 'NOOFSITES') {
            valueField = 'NoOfUnits';
            title = 'Number of Sites';
            labelText = '[[NoOfUnits]] units';
        }
        if (this.selectedDerValueType === 'STORAGECAPACITY') {
            valueField = 'StorageCapacity';
            title = 'kVAh';
            labelText = '[[StorageCapacity]]kVAh';
        }

        chartObject = null;

        return {
            type: 'serial',
            fontSize: 11,
            marginRight: 0,
            marginLeft: 0,
            marginTop: 10,
            marginBottom: 0,
            addClassNames: true,
            autoMargin: false,
            autoMarginOffset: 10,
            allLabels: { color: '#8ea9a' }, 
            seriesField: 'State',
            seriesColour: 'Colour',
            seriesLabelColour: 'LabelColour',
            seriesValueField: valueField,
            showLabelText: false,
            panEventsEnabled: false,
            numberFormatter: {
                precision: 0,
                decimalSeparator: '.',
                thousandsSeparator: ','
            },
            //labelText: labelText,
            labelsEnabled: false,
            legend: {
                enabled: true ,
                align: 'center',
                markerLabelGap: 10
            },
            valueAxes: [{
                position: "left",
                stackType: 'regular',
                labelsEnabled: true,
                gridAlpha: 0.1,
                axisAlpha: 0.1,
                color: '#8ea9a9',
                title: title,
                titleColor: '#2c0831'
            }], 
            categoryAxis: {
                labelRotation: 45,
                color: '#8ea9a9',
                gridAlpha: 0.1,
                labelsEnabled: true,
                axisAlpha: 0.1
            },
            categoryField: "Period",
            dataProvider: yearlyData,
            chartCursor: {
                cursorColor: '#8ea9a9',  
                categoryBalloonAlpha: 1,
                cursorAlpha: 0.2,
                fullWidth: true
            }

        };

    }

    totalsChartRendered(chart: any) {
        this.chartObject = chart;
    }
    yearlyAdditionsChartRendered(chart: any) {
        this.yearlyAdditionsChartObject = chart;
    }
    yearlyCumulativeChartRendered(chart: any) {
        this.yearlyCumulativeChartObject = chart;
    }

    selectInstallationType(installationType: string) {
        this.selectedInstallationType = installationType;
    }
    selectSource(selectedSource: string) {
        this.selectedSource = selectedSource;
    }
    selectDerValueType(valueType: string) {
        this.selectedDerValueType = valueType;
    }

    toggleFullscreen() {
        (screenfull as any).toggle();
    }

    waDataToggle() {
        this.fetchData(); 
    }

    async download() {
        let IdData = await DataManager.addEndPoint('../../data/der/DER-DATA.csv', undefined, undefined, undefined, 'csv', 'yyyy-MM-dd', ['DATETIME']);
        let dynamicViewData: CustomDynamicView<any> = DataManager.getDynamicView(IdData, "DerData");
        // Make sure WA data is not included in downloads
        dynamicViewData.applyWhere(function aCustomFilter(obj) {
            return obj.State != 'WA';
        });
        let dataView = dynamicViewData.data();
        const columns = [
            {
                field: 'Period'
            },
            {
                field: 'State'
            },
            {
                field: 'NoOfUnits'
            },
            {
                field: 'Source'
            },
            {
                field: 'RatedCapacity'
            },
            {
                field: 'StorageCapacity'
            },
            {
                field: 'InstallationType'
            }
        ];
        let csv = arrayToCsv(dataView, columns);
        downloadCsv(`AEMO_DER_${formatDate(new Date(), 'yyyyMMddHHmm')}.csv`, csv);
    }

}



// WEBPACK FOOTER //
// src/views/electricity/der/der-sites.ts